import { HandoverReportDetails } from '../../types/handover';
import { FlowTextOptions, PdfDocument } from '../../utils/pdf/PdfDocument';
import { REPORT_MARGINS, drawReportLogo, drawReportPageChrome, getIcelandairLogo } from '../../utils/pdf/reportChrome';

type HandoverPdfArgs = {
    report: HandoverReportDetails;
    /** Heading of the document, e.g. "Line Planner Handover" (config.dialogTitle). */
    title: string;
    /** Heading above the crew list, e.g. "Shift leaders" (config.employeeLabel). */
    employeeLabel: string;
};

// The standard PDF fonts are WinAnsi-encoded, so the Icelandic letters render without
// embedding a font. Times-Bold is used for headings to match the other PDFs in the app.
const styles: Record<string, FlowTextOptions> = {
    h1: { font: 'Times-Bold', size: 18, align: 'center' },
    h2: { font: 'Times-Bold', size: 14, align: 'center', marginTop: 18, marginBottom: 6 },
    metaLabel: { font: 'Times-Bold', size: 11 },
    metaValue: { size: 11 },
    body: { size: 11 },
    headerCell: { font: 'Times-Bold', size: 11 },
    cell: { size: 10 },
    emptyRow: { size: 10, align: 'center', color: '#808080' },
};

/** "Day" / "Night", or null when the report does not carry a day/night flag at all. */
const shiftText = (report: HandoverReportDetails) =>
    report.day == null ? null : report.day === true ? 'Day' : 'Night';

const textOrDash = (value: string | undefined | null) =>
    value === undefined || value === null || value.trim().length === 0 ? '-' : value;

/**
 * The handover report as an A4 document. Sections follow the dialog: shift date, day/night,
 * to be read by, issues, crew and general notes. Crew avatars are intentionally left out.
 * Takes a moment on reports with many issues.
 */
export const buildHandoverPdfBlob = async ({ report, title, employeeLabel }: HandoverPdfArgs): Promise<Blob> => {
    const issues = report.handover_issues ?? [];
    const employees = report.handover_employees ?? [];
    const shift = shiftText(report);
    const logo = await getIcelandairLogo();

    const doc = new PdfDocument({ margins: REPORT_MARGINS, title });

    drawReportLogo(doc, logo);
    doc.text(title, { ...styles.h1, marginBottom: 20 });

    const metaRow = (label: string, value: string) => doc.columns([
        { width: 110, render: () => doc.text(label, styles.metaLabel) },
        { width: doc.width - 110, render: () => doc.text(value, styles.metaValue) },
    ]);
    const metaRows: [string, string][] = [
        ['Shift date:', textOrDash(report.created_date_text)],
        ...(shift !== null ? [['Shift:', shift] as [string, string]] : []),
        ['To be read by:', textOrDash(report.to_be_read_by_name)],
        ['Created by:', textOrDash(report.supervisor_name)],
        // An unfinished report must be recognisable as unfinished once printed.
        ['Status:', report.is_submitted === true ? 'Submitted' : 'Open'],
    ];
    metaRows.forEach(([label, value]) => {
        metaRow(label, value);
        doc.moveDown(3);
    });
    doc.moveDown(4);

    doc.text('Issues', styles.h2);
    const colWidths = [doc.width * 0.08, doc.width * 0.17, doc.width * 0.75];
    const cellPadding = { paddingX: 4, paddingY: 4 };
    doc.row(
        ['#', 'Tail', 'Issue'].map((text, i) => ({ width: colWidths[i], text, style: styles.headerCell })),
        { ...cellPadding, background: '#ebebeb', borderBottom: { color: '#9e9e9e', width: 1 } },
    );
    if (issues.length === 0) {
        doc.moveDown(6);
        doc.text('No issues', { ...styles.emptyRow, marginBottom: 6 });
    } else {
        issues.forEach((issue, index) => doc.row(
            [String(index + 1), issue.tail_number ?? '', issue.issue ?? ''].map((text, i) => ({ width: colWidths[i], text, style: styles.cell })),
            {
                ...cellPadding,
                // Mirrors the dialog table's bg-black/[0.04] zebra striping.
                background: index % 2 ? '#f5f5f5' : undefined,
                borderBottom: { color: '#e0e0e0', width: 1 },
            },
        ));
    }

    doc.text(employeeLabel, styles.h2);
    doc.text(employees.length === 0 ? '-' : employees.map(employee => employee.name).join(', '), styles.body);

    doc.text('General notes', styles.h2);
    doc.text(textOrDash(report.general_notes), styles.body);

    drawReportPageChrome(doc, `Icelandair | generated ${new Date().toLocaleString()}`, 10);
    return doc.toBlob();
};

/** e.g. "Line Planner Handover 23.09.2026 14-30.pdf". Colons and slashes break downloads. */
export const handoverPdfFileName = (report: HandoverReportDetails, title: string) => {
    const datePart = (report.created_date_text ?? '').replace(/[:/\\]/g, '-').trim();
    return datePart.length === 0 ? `${title}.pdf` : `${title} ${datePart}.pdf`;
};
